import { CalendarDays, MessageSquareQuote } from 'lucide-react'
import { usePanel } from '@/app/panel'
import { HoverCard } from '@/components/ui/overlays'
import type { AnswerCitation, CitationTurn } from '@/data/chat'
import { cn } from '@/lib/utils'

/** `934` → `15:34`; `3701` → `1:01:41`. */
export function clock(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return ''
  const total = Math.max(0, Math.round(seconds))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = total % 60
  const mm = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes)
  return `${hours > 0 ? `${hours}:` : ''}${mm}:${String(secs).padStart(2, '0')}`
}

/**
 * What the marker shows.
 *
 * The meeting's number, always — it is short, it is the same number in every
 * answer that cites this meeting, and a reader who sees `12` three times in a
 * paragraph learns that three claims rest on one conversation. The turn is what
 * the number cannot show without becoming unreadable inline, so it goes in the
 * preview instead.
 */
function label(citation: AnswerCitation): string {
  return citation.meetingNumber > 0 ? String(citation.meetingNumber) : '·'
}

function Turn({ turn }: { turn: CitationTurn }) {
  return (
    <div className={cn('text-2xs leading-relaxed', turn.context && 'opacity-45')}>
      <span className="font-medium text-[var(--color-fg)]">{turn.speaker}</span>
      {turn.startSeconds !== null ? (
        <span className="tnum ml-1.5 text-[var(--color-fg-subtle)]">{clock(turn.startSeconds)}</span>
      ) : null}
      <p className="mt-0.5 text-[var(--color-fg-muted)]">{turn.text}</p>
    </div>
  )
}

/**
 * The evidence, on hover.
 *
 * This is the whole point of citing something smaller than a meeting: the
 * reader can check the claim without leaving the sentence. The cited turns are
 * at full strength and their neighbours are dimmed, so it is visible which
 * words the answer actually stood on rather than which words were nearby.
 */
function Preview({ citation }: { citation: AnswerCitation }) {
  const date = citation.startsAt ? new Date(citation.startsAt).toLocaleDateString() : ''
  const time = [clock(citation.startSeconds), clock(citation.endSeconds)]
    .filter(Boolean)
    .join('–')

  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-2">
        <span className="min-w-0 flex-1 truncate text-2xs font-medium text-[var(--color-fg)]">
          {citation.title}
        </span>
        <span className="tnum shrink-0 text-2xs text-[var(--color-fg-subtle)]">{date}</span>
      </div>
      {citation.headline || time ? (
        <div className="flex items-baseline gap-2 text-2xs text-[var(--color-fg-subtle)]">
          {citation.headline ? <span className="min-w-0 flex-1">{citation.headline}</span> : null}
          {time ? <span className="tnum shrink-0">{time}</span> : null}
        </div>
      ) : null}

      {citation.turns.length > 0 ? (
        <div className="space-y-1.5 border-t border-[var(--color-border-subtle)] pt-2">
          {citation.turns.map((turn) => (
            <Turn key={turn.turnId} turn={turn} />
          ))}
        </div>
      ) : (
        <p className="border-t border-[var(--color-border-subtle)] pt-2 text-2xs text-[var(--color-fg-subtle)]">
          Cites the meeting as a whole.
        </p>
      )}

      <p className="text-[10px] text-[var(--color-fg-subtle)]">Click to open the meeting</p>
    </div>
  )
}

/**
 * The `[[12.137]]` in a sentence.
 *
 * It opens the meeting in the side panel rather than navigating: the whole
 * point of citing is that the claim and its evidence are on screen together,
 * and a link that replaces the answer with its source destroys exactly that.
 *
 * The number is kept as the visible content. A dot, an icon or the meeting's
 * name would each be prettier and none of them survives being read aloud, being
 * copied into an email, or appearing twice in one paragraph.
 */
export function InlineCitation({ citation, claim }: { citation: AnswerCitation; claim?: string }) {
  const { open, payload } = usePanel()
  const active = payload?.type === 'meeting' && payload.meetingId === citation.meetingId

  const describe =
    citation.kind === 'turn' && citation.turns.length > 0
      ? `${citation.title}, ${citation.turns.find((turn) => !turn.context)?.speaker ?? ''}`.trim()
      : citation.title

  return (
    <HoverCard content={<Preview citation={citation} />}>
      <button
        type="button"
        onClick={() =>
          open({
            type: 'meeting',
            meetingId: citation.meetingId,
            claim,
            ...(citation.startSeconds !== null ? { atSeconds: citation.startSeconds } : {}),
            ...(citation.turns.length > 0 ? { turns: citation.turns } : {}),
          })
        }
        aria-label={`Source ${label(citation)}: ${describe}`}
        className={cn(
          'mx-0.5 inline-grid h-4 min-w-4 place-items-center rounded-[var(--radius-xs)] px-1',
          'align-[0.1em] text-[10px] font-medium tabular-nums outline-none transition-colors duration-100',
          active
            ? 'bg-[var(--color-citation)] text-white'
            : 'bg-[var(--color-citation-bg)] text-[var(--color-citation)] hover:bg-[var(--color-citation)] hover:text-white'
        )}
      >
        {label(citation)}
      </button>
    </HoverCard>
  )
}

/**
 * The sources under an answer.
 *
 * Listed once at the end rather than repeated inline, because the same meeting
 * is usually cited by several sentences and a card per marker would bury the
 * prose it belongs to. One row per MEETING, not per reference: an answer that
 * cites four turns of one stand-up has one source, and saying so four times is
 * how a three-line answer grows a longer bibliography than body.
 */
export function CitationList({ citations }: { citations: AnswerCitation[] }) {
  const { open, payload } = usePanel()

  const byMeeting = new Map<string, { citation: AnswerCitation; refs: number }>()
  for (const citation of citations) {
    const existing = byMeeting.get(citation.meetingId)
    if (existing) existing.refs += 1
    else byMeeting.set(citation.meetingId, { citation, refs: 1 })
  }

  return (
    <ul className="mt-3 space-y-1 border-t border-[var(--color-border-subtle)] pt-2.5">
      {[...byMeeting.values()].map(({ citation, refs }) => {
        const active = payload?.type === 'meeting' && payload.meetingId === citation.meetingId
        return (
          <li key={citation.meetingId}>
            <button
              type="button"
              onClick={() => open({ type: 'meeting', meetingId: citation.meetingId })}
              className={cn(
                'flex w-full items-center gap-2 rounded-[var(--radius-sm)] px-1.5 py-1 text-left',
                active
                  ? 'bg-[var(--color-surface-hover)]'
                  : 'hover:bg-[var(--color-surface-hover)]'
              )}
            >
              <span className="tnum w-3.5 shrink-0 text-2xs text-[var(--color-fg-subtle)]">
                {label(citation)}
              </span>
              <CalendarDays className="size-3 shrink-0 text-[var(--color-fg-subtle)]" />
              <span className="min-w-0 flex-1 truncate text-2xs text-[var(--color-fg-muted)]">
                {citation.title}
                {citation.headline ? (
                  <span className="text-[var(--color-fg-subtle)]"> · {citation.headline}</span>
                ) : null}
              </span>
              {refs > 1 ? (
                <span className="flex shrink-0 items-center gap-0.5 text-2xs text-[var(--color-fg-subtle)]">
                  <MessageSquareQuote className="size-3" />
                  {refs}
                </span>
              ) : null}
              <span className="tnum shrink-0 text-2xs text-[var(--color-fg-subtle)]">
                {citation.startsAt ? new Date(citation.startsAt).toLocaleDateString() : ''}
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}
